import { Controller, UseFieldArrayReturn, useFieldArray } from "react-hook-form";
import { Customer } from "src/api/generated/erp/db/types/tables/customer.ts";
import { FormSelectField } from "../../../../common/forms/fields/FormSelectField.tsx";
import i18n from "i18next";
import { FormTextField } from "../../../../common/forms/fields/FormTextField.tsx";
import { requireRule } from "../../../../common/forms/validation.ts";
import { getCustomerTypeLabels } from "src/api/generated/erp/db/types/enums/customerType.ts";
import { getLanguageLabels } from "src/api/generated/erp/db/types/enums/language.ts";
import { FormNumberField } from "../../../../common/forms/fields/FormNumberField.tsx";
import { getInvoiceChannelLabels } from "src/api/generated/erp/db/types/enums/invoiceChannel.ts";
import { CustomerAddress } from "src/api/generated/erp/db/types/tables/customerAddress.ts";
import { Divider } from "@mui/material";
import { FormCheckbox } from "../../../../common/forms/fields/FormCheckbox.tsx";
import { FormEnumSelectField } from "../../../../common/forms/fields/FormEnumSelectField.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { FormAddressAutofill } from "src/components/common/forms/fields/FormAddressAutofill.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { CustomerFormFieldToHideType, TenantConfig } from "src/tenantCustomizations/TenantCustomizations.ts";
import { ContactCreateInput } from "src/api/generated/erp/contacts/model/contactCreateInput.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoCheckbox } from "../../../../common/inputFields/AavoCheckbox.tsx";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import { CustomerFormApi_InitData } from "src/api/generated/erp/sales/customer/api/customerFormApi.ts";

export interface CustomerFormValues extends Customer {
	deliveryAddress: CustomerAddress;
	invoiceAddress: CustomerAddress;
	invoiceSameAsDelivery: boolean;
	contacts: ContactCreateInput[];
	defaultContactIndex: number;
}

export interface CustomerFormContentProps extends AavoFormContentParams<CustomerFormValues> {
	isNewRecord: boolean;
	initData: Pick<
		CustomerFormApi_InitData,
		| "customerGroupOptions"
		| "paymentTermOptions"
		| "currencyOptions"
		| "transportRouteOptions"
		| "customerPriceGroupOptions"
		| "eInvoiceOperatorOptions"
		| "salespersonOptions"
		| "deliveryTermOptions"
	>;
}

export const CustomerFormContent = ({
	control,
	watch,
	setValue,
	isNewRecord,
	initData: {
		customerGroupOptions,
		paymentTermOptions,
		currencyOptions,
		transportRouteOptions,
		customerPriceGroupOptions,
		eInvoiceOperatorOptions,
		salespersonOptions,
		deliveryTermOptions,
	},
}: CustomerFormContentProps) => {
	const customerType = watch("customerType");
	const invoiceSameAsDelivery = watch("invoiceSameAsDelivery");
	const invoiceChannel = watch("invoiceChannel");
	const hasPermission = useHasPermission();

	const contactsFieldArray = useFieldArray({
		control,
		name: "contacts",
	});

	const { tenantConfig } = useTenantCustomizations();

	return (
		<>
			<FormTextField
				control={control}
				name={"customerName"}
				autoFocus={isNewRecord}
				label={i18n.t("name")}
				rules={requireRule()}
			/>
			{showField(tenantConfig, "customerGroupId") && (
				<FormSelectField
					control={control}
					name={"customerGroupId"}
					label={i18n.t("customer_group")}
					options={customerGroupOptions}
					getOptionKey={(option) => option.customerGroupId}
					getOptionLabel={(option) => option.name ?? ""}
				/>
			)}
			<FormEnumSelectField
				control={control}
				name={"customerType"}
				label={i18n.t("customer_type")}
				options={getCustomerTypeLabels()}
				rules={requireRule()}
				disableClearable
			/>
			{customerType === "COMPANY" && (
				<FormTextField control={control} name={"businessId"} label={i18n.t("business_id")} />
			)}
			<FormEnumSelectField
				control={control}
				name={"language"}
				label={i18n.t("language")}
				options={getLanguageLabels()}
				rules={requireRule()}
				startNewGridRow
			/>
			<FormSelectField
				control={control}
				name={"salespersonId"}
				label={i18n.t("salesperson")}
				options={salespersonOptions}
				getOptionKey={(o) => o.salespersonId}
				getOptionLabel={(o) => o.name}
				disabled={!hasPermission(Permissions.Sales.Customers.MANAGE_ALL)}
			/>
			{showField(tenantConfig, "customerPriceGroupId") && (
				<FormSelectField
					control={control}
					name={"customerPriceGroupId"}
					label={i18n.t("price_group")}
					options={customerPriceGroupOptions}
					getOptionKey={(group) => group.customerPriceGroupId}
					getOptionLabel={(group) => group.name ?? ""}
					startNewGridRow
				/>
			)}
			{showField(tenantConfig, "discountPercentage") && (
				<FormNumberField
					control={control}
					name={"discountPercentage"}
					label={i18n.t("discount_percentage")}
					rules={requireRule()}
				/>
			)}
			<FormSelectField
				control={control}
				name={"paymentTermId"}
				label={i18n.t("payment_term")}
				rules={requireRule()}
				options={paymentTermOptions}
				getOptionKey={(term) => term.paymentTermId}
				getOptionLabel={(term) => term.paymentTerm ?? ""}
				disableClearable
			/>
			<FormSelectField
				control={control}
				name={"currencyId"}
				label={i18n.t("currency")}
				rules={requireRule()}
				options={currencyOptions}
				getOptionKey={(currency) => currency.currencyId}
				getOptionLabel={(currency) => currency.currencyCode}
				disableClearable
			/>
			{showField(tenantConfig, "deliveryTermsId") && (
				<FormSelectField
					control={control}
					name={"deliveryTermsId"}
					label={i18n.t("delivery_term")}
					options={deliveryTermOptions}
					getOptionKey={(term) => term.deliveryTermsId}
					getOptionLabel={(term) => `${term.deliveryTermsCode} - ${term.deliveryTermsDescription}`}
				/>
			)}
			{showField(tenantConfig, "deliveryTermsDestination") && (
				<FormTextField
					control={control}
					name={"deliveryTermsDestination"}
					label={i18n.t("delivery_terms_destination")}
				/>
			)}
			<FormEnumSelectField
				control={control}
				name={"invoiceChannel"}
				label={i18n.t("invoice_channel")}
				options={getInvoiceChannelLabels()}
				disableClearable
			/>
			{invoiceChannel === "EINVOICE" && (
				<FormSelectField
					control={control}
					name={"eInvoiceOperatorId"}
					label={i18n.t("e_invoice_operator")}
					options={eInvoiceOperatorOptions}
					getOptionKey={(o) => o.eInvoiceOperatorId}
					getOptionLabel={(o) => o.eInvoiceOperatorName}
				/>
			)}
			{invoiceChannel === "EINVOICE" && (
				<FormTextField control={control} name={"einvoiceAddress"} label={i18n.t("e_invoice_address")} />
			)}
			{showField(tenantConfig, "externalId") && (
				<FormTextField control={control} name={"externalId"} label={i18n.t("external_id")} />
			)}
			<FormTextField control={control} name={"note"} label={i18n.t("note")} multiline spanGridColumns />
			{isNewRecord && (
				<>
					<>
						<AddressForm
							control={control}
							setValue={setValue}
							name={"deliveryAddress"}
							label={i18n.t("delivery_address")}
						/>
						{showField(tenantConfig, "deliveryAddress.transportRouteId") && (
							<FormSelectField
								control={control}
								name={"deliveryAddress.transportRouteId"}
								label={i18n.t("transport_route")}
								options={transportRouteOptions}
								getOptionKey={(route) => route.transportRouteId}
								getOptionLabel={(route) => route.name ?? ""}
							/>
						)}
						<FormCheckbox
							label={i18n.t("invoice_address_same_as_delivery_address")}
							control={control}
							name={"invoiceSameAsDelivery"}
						/>
						{!invoiceSameAsDelivery && (
							<AddressForm
								control={control}
								setValue={setValue}
								name={"invoiceAddress"}
								label={i18n.t("invoice_address")}
							/>
						)}
					</>
					<ContactsForm
						control={control}
						watch={watch}
						setValue={setValue}
						contactsFieldArray={contactsFieldArray}
					/>
				</>
			)}
		</>
	);
};

export const showField = (tenantConfig: TenantConfig, fieldName: CustomerFormFieldToHideType): boolean => {
	const fieldsToHide = tenantConfig.erp.customerFormFieldsToHide;
	const isInList = fieldsToHide.find((hideFieldName) => hideFieldName == fieldName) != undefined;
	return !isInList;
};

interface AddressFormProps extends Pick<CustomerFormContentProps, "control" | "setValue"> {
	name: "deliveryAddress" | "invoiceAddress";
	label: string;
}

const AddressForm = ({ control, name, label, setValue }: AddressFormProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const useDefaultContact = tenantConfig.erp.customerFormUseDefaultContact;

	return (
		<>
			<Divider
				sx={{
					gridColumn: "1 / -1",
					color: "primary.main",
				}}
			>
				{label}
			</Divider>
			<FormAddressAutofill
				control={control}
				name={`${name}.address_1`}
				label={i18n.t("address_1")}
				rules={requireRule()}
				onSelectLocation={(location) => {
					setValue(`${name}.postalCode`, location.postcode);
					setValue(`${name}.city`, location.address_level2);
					return location.address_line1;
				}}
			/>
			<FormTextField control={control} name={`${name}.postalCode`} label={i18n.t("postal_code")} />
			<FormTextField control={control} name={`${name}.city`} label={i18n.t("city")} />
			<FormCountrySelectField
				control={control}
				name={`${name}.country`}
				label={i18n.t("country")}
			/>
			{!useDefaultContact && (
				<FormTextField control={control} name={`${name}.contact`} label={i18n.t("contact_person")} />
			)}
			{!useDefaultContact && <FormTextField control={control} name={`${name}.phone`} label={i18n.t("phone")} />}
			{!useDefaultContact && <FormTextField control={control} name={`${name}.email`} label={i18n.t("email")} />}
			{showField(tenantConfig, `${name}.addressName`) && (
				<FormTextField control={control} name={`${name}.name`} label={i18n.t("address_description")} />
			)}
		</>
	);
};

interface ContactsFormProps extends Pick<CustomerFormContentProps, "control" | "watch" | "setValue"> {
	contactsFieldArray: UseFieldArrayReturn<CustomerFormValues, "contacts">;
}

const ContactsForm = ({ control, watch, setValue, contactsFieldArray }: ContactsFormProps) => {
	return (
		<>
			<Divider
				sx={{
					gridColumn: "1 / -1",
					color: "primary.main",
				}}
			>
				{i18n.t("contacts")}
			</Divider>
			<VerticalBox
				sx={{
					gridColumn: "1 / -1",
					gap: 1,
				}}
			>
				{contactsFieldArray.fields.map((field, index) => (
					<SingleContactForm
						key={field.id}
						control={control}
						watch={watch}
						setValue={setValue}
						index={index}
						contactsFieldArray={contactsFieldArray}
					/>
				))}
				<AavoButton
					icon={faPlus}
					label={i18n.t("add_contact")}
					variant={"outlined"}
					onClick={() =>
						contactsFieldArray.append({
							name: "",
							designation: "",
							phone: "",
							email: "",
							note: "",
							isContractSigner: false,
							isDefaultContact: false,
						})
					}
					sx={{
						alignSelf: "flex-start",
					}}
				/>
			</VerticalBox>
		</>
	);
};

interface SingleContactFormProps extends Pick<CustomerFormContentProps, "control" | "watch" | "setValue"> {
	index: number;
	contactsFieldArray: UseFieldArrayReturn<CustomerFormValues, "contacts">;
}

const SingleContactForm = ({ control, index, contactsFieldArray, watch, setValue }: SingleContactFormProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const showContractSignerField = tenantConfig.erp.customerOfferContractSigningEnabled;
	const useDefaultContact = tenantConfig.erp.customerFormUseDefaultContact;
	const defaultContactIndex = watch("defaultContactIndex");

	return (
		<HorizontalBox
			sx={{
				border: "1px solid",
				borderColor: "grey.400",
				borderRadius: 1,
				padding: 1,
				gap: 1,
				alignItems: "flex-start",
			}}
		>
			<VerticalBox
				sx={{
					flex: 1,
					gap: 1,
				}}
			>
				<HorizontalBox sx={{ gap: 1 }}>
					<FormTextField
						control={control}
						name={`contacts.${index}.name`}
						label={i18n.t("name")}
						rules={requireRule()}
						sx={{ flex: 1 }}
					/>
					<FormTextField
						control={control}
						name={`contacts.${index}.designation`}
						label={i18n.t("designation")}
						sx={{ flex: 1 }}
					/>
				</HorizontalBox>
				<HorizontalBox sx={{ gap: 1 }}>
					<FormTextField
						control={control}
						name={`contacts.${index}.email`}
						label={i18n.t("email")}
						sx={{ flex: 1 }}
					/>
					<FormTextField
						control={control}
						name={`contacts.${index}.phone`}
						label={i18n.t("phone")}
						sx={{ flex: 1 }}
					/>
				</HorizontalBox>
				<FormTextField control={control} name={`contacts.${index}.note`} label={i18n.t("note")} />
				{showContractSignerField && (
					<FormCheckbox
						control={control}
						name={`contacts.${index}.isContractSigner`}
						label={i18n.t("is_contract_signer")}
					/>
				)}
				{useDefaultContact && (defaultContactIndex === -1 || defaultContactIndex === index) && (
					<Controller
						control={control}
						name={`defaultContactIndex`}
						render={({ field }) => (
							<AavoCheckbox
								checked={field.value === index}
								onChange={(checked) => {
									if (checked) {
										field.onChange(index);
									} else {
										field.onChange(-1);
									}
								}}
								label={i18n.t("default_contact")}
							/>
						)}
					/>
				)}
			</VerticalBox>
			<AavoButton
				icon={faTimes}
				onClick={() => {
					if (defaultContactIndex === index) {
						setValue("defaultContactIndex", -1);
					} else if (index < defaultContactIndex) {
						setValue("defaultContactIndex", defaultContactIndex - 1);
					}
					contactsFieldArray.remove(index);
				}}
			/>
		</HorizontalBox>
	);
};
